import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Clock, MapPin, Play, Square, CheckCircle2 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";
import { NotificationsBell } from "@/components/NotificationsBell";

interface Row {
  id: string;
  title: string;
  status: string;
  budget_cents: number;
  location: string | null;
  start_time: string | null;
  gps_lat: number | null;
  gps_lng: number | null;
  shift?: {
    id: string;
    status: string;
    clock_in_at: string | null;
    clock_out_at: string | null;
    total_minutes: number;
  } | null;
}

const useNow = () => {
  const [t, setT] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setT(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
};

const fmtElapsed = (start: string, now: number) => {
  const ms = Math.max(0, now - Date.parse(start));
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600).toString().padStart(2, "0");
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${h}:${m}:${sec}`;
};

const MyGigs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const now = useNow();

  const load = useCallback(async () => {
    if (!user) return;
    const { data: gigs } = await supabase
      .from("gigs")
      .select("id, title, status, budget_cents, location, start_time, gps_lat, gps_lng")
      .eq("worker_id", user.id)
      .order("created_at", { ascending: false });
    const list = (gigs as Row[]) ?? [];
    if (list.length) {
      const { data: shifts } = await supabase
        .from("gig_shifts")
        .select("id, gig_id, status, clock_in_at, clock_out_at, total_minutes")
        .eq("worker_id", user.id)
        .in("gig_id", list.map((g) => g.id));
      const map = new Map((shifts ?? []).map((s: any) => [s.gig_id, s]));
      list.forEach((g) => (g.shift = (map.get(g.id) as any) ?? null));
    }
    setRows(list);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const getLocation = (): Promise<{ lat: number; lng: number } | null> =>
    new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => res(null),
        { enableHighAccuracy: true, timeout: 8000 },
      );
    });

  const act = async (gigId: string, action: "clock_in" | "clock_out") => {
    setBusy(gigId);
    const loc = await getLocation();
    const { data, error } = await supabase.functions.invoke("gig-shift-action", {
      body: { gig_id: gigId, action, lat: loc?.lat, lng: loc?.lng },
    });
    setBusy(null);
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? error?.message ?? "Failed");
      return;
    }
    toast.success(action === "clock_in" ? "Clocked in" : "Shift complete");
    load();
  };

  const active = rows.filter((r) => r.status === "in_progress" || r.status === "pending_approval");
  const upcoming = rows.filter((r) =>
    (r.status === "in_progress" || r.status === "pending_approval") &&
    r.start_time && Date.parse(r.start_time) > Date.now()
  );
  const history = rows.filter((r) => ["completed", "cancelled", "disputed"].includes(r.status));

  if (loading)
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate("/wallet")} right={<NotificationsBell />} />
        <div className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileShell>
    );

  const Card = ({ g }: { g: Row }) => {
    const onShift = g.shift?.clock_in_at && !g.shift?.clock_out_at;
    return (
      <div className="rounded-2xl bg-card border border-border p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/gigs/${g.id}`} className="font-display font-bold text-base leading-tight">
            {g.title}
          </Link>
          <span className="font-display font-extrabold text-primary text-sm">{formatMoney(g.budget_cents)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="uppercase font-semibold">{g.status.replace(/_/g, " ")}</span>
          {g.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {g.location}
            </span>
          )}
          {g.start_time && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {new Date(g.start_time).toLocaleString()}
            </span>
          )}
        </div>

        {onShift && g.shift?.clock_in_at && (
          <div className="rounded-xl bg-primary/10 border border-primary/30 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-primary">On shift</span>
            <span className="font-mono font-bold text-primary text-lg">
              {fmtElapsed(g.shift.clock_in_at, now)}
            </span>
          </div>
        )}

        {g.status === "in_progress" && (
          <div className="flex gap-2 pt-1">
            {!g.shift?.clock_in_at && (
              <Button
                size="sm"
                className="flex-1 rounded-full"
                disabled={busy === g.id}
                onClick={() => act(g.id, "clock_in")}
              >
                <Play className="h-4 w-4 mr-1" /> Clock in
              </Button>
            )}
            {onShift && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1 rounded-full"
                disabled={busy === g.id}
                onClick={() => act(g.id, "clock_out")}
              >
                <Square className="h-4 w-4 mr-1" /> Clock out
              </Button>
            )}
            {g.shift?.clock_out_at && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> {g.shift.total_minutes} min worked
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate("/wallet")} right={<NotificationsBell />} />
      <div className="px-4 pt-2 pb-24">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-display font-extrabold text-2xl">My Gigs</h1>
          <Link to="/gigs" className="text-xs text-primary font-semibold">
            Browse jobs →
          </Link>
        </div>
        <Tabs defaultValue="active">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="active">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="history">History ({history.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="space-y-3 mt-3">
            {active.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No active gigs.</p>
            ) : (
              active.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
          <TabsContent value="upcoming" className="space-y-3 mt-3">
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Nothing upcoming.</p>
            ) : (
              upcoming.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
          <TabsContent value="history" className="space-y-3 mt-3">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No completed gigs yet.</p>
            ) : (
              history.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MobileShell>
  );
};

export default MyGigs;
